// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const { pickUpWarehouse, properties } = getSession(state);
    const {
        customerSettings: { allowShipToAddressEdit },
    } = getSettingsCollection(state);

    return {
        pickUpWarehouse,
        currentWarehouse: properties?.currentWarehouse,
        allowShipToAddressEdit,
    };
};

const mapDispatchToProps = {
    setStorePickupOverlayIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HeaderCurrentWarehouseStyles {
    titleTypography?: TypographyPresentationProps;
    currentWarehouseDescriptionClickable?: ClickablePresentationProps;
}

export const headerCurrentWarehouseStyles: HeaderCurrentWarehouseStyles = {};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderCurrentWarehouse: FC<Props> = ({
    setStorePickupOverlayIsOpen,
    pickUpWarehouse,
    currentWarehouse,
    allowShipToAddressEdit,
}) => {
    const editPickUpWarehouseClickHandler = () => {
        setStorePickupOverlayIsOpen({ isOpen: true, showBranchesOnly: true } as any);
    };

    const styles = headerCurrentWarehouseStyles;

    return (
        <>
            {/*  please also make changes to `NavigationDrawer.tsx` when you change this. */}
            <Typography {...styles.titleTypography} data-test-selector="header_currentWarehouseLabel">
                {allowShipToAddressEdit ? (
                    <>
                        {translate("Currently Shopping:")}
                        <Clickable
                            {...styles.currentWarehouseDescriptionClickable}
                            onClick={editPickUpWarehouseClickHandler}
                            data-test-selector="header_currentWarehouseDescription"
                        >
                            {pickUpWarehouse?.description || "--"}
                        </Clickable>
                    </>
                ) : (
                    <>
                        {siteMessage("Custom_Header_Default_Warehouse_Label")}
                        <Typography
                            {...styles.currentWarehouseDescriptionClickable}
                            data-test-selector="header_currentWarehouse"
                        >
                            {currentWarehouse || "--"}
                        </Typography>
                    </>
                )}
            </Typography>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderCurrentWarehouse),
    definition: {
        displayName: "Header Current Warehouse",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
